// Same URL-detection pattern as
// src/app/templates/portesa/.../includes/product-timeline-item/product-timeline-item.component.ts
// (setVideoLink) and .../product-details/product-details.component.ts (formateVideoURL),
// ported here as a pure, DomSanitizer-free function so historia/mapa do not import from
// portesa's internals and portesa itself stays untouched.
//
// Per spec §6/§9 (Historia y Mapa): un enlace de vídeo que no se puede incrustar (p. ej. un
// reel de Instagram) se mantiene como enlace externo en vez de romper el módulo.

export interface VideoEmbedResult {
  embedUrl: string | null
  isEmbeddable: boolean
  originalUrl: string
}

const YOUTUBE_REGEX = /^(https?:\/\/)?(www\.)?(youtube|youtu|youtube-nocookie)\.(com|be)\/(watch\?v=|embed\/|v\/)?([a-zA-Z0-9_-]{11})/
const VIMEO_REGEX = /^(https?:\/\/)?(www\.)?(vimeo)\.com\/(\d+)($|\/)/
const DAILYMOTION_REGEX = /^(https?:\/\/)?(www\.)?(dailymotion)\.com\/video\/(\w+)(.*)/
const DIRECT_VIDEO_REGEX = /\.(mp4|ogg|webm)$/i

// Base for resolving a scheme-less link ("cdn.portesa.es/clip.mp4"), which the URL-shaped
// regex this replaced used to accept. It is never navigated to; only the resulting
// protocol is read.
const RELATIVE_BASE = 'https://relative.invalid'

// Guards every link that ends up in an <iframe src> through bypassSecurityTrustResourceUrl.
//
// It replaces this regex, which lived in eight copies across the templates:
//   /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w.-]*)*\??([\w.-]+=[\w.-]+&?)*$/
// Two nested quantifiers over overlapping character classes make a link that ALMOST
// matches backtrack exponentially: measured on this machine, an http URL with 20 path
// characters and one invalid character took 97 ms, 24 took 1.6 s, 26 took 6.6 s. videoLink
// is tenant-authored text from extraDataJson and this runs in the browser of whoever scans
// the QR, so a 40-character path is a frozen tab for every visitor of that passport.
//
// What the check is actually for is the scheme: `javascript:` and `data:text/html` must
// never reach the bypass. Parsing gives that exactly, in constant time, and drops the
// regex's incidental TLD-shape requirement, which protected nothing.
export function isEmbeddableHttpUrl(url: string | null | undefined): boolean {
  if (!url) return false

  try {
    const { protocol } = new URL(url.trim(), RELATIVE_BASE)
    return protocol === 'https:' || protocol === 'http:'
  } catch {
    // An unparseable link is not embeddable. Nothing is swallowed: false IS the answer,
    // and the caller renders the link as external text instead of an iframe.
    return false
  }
}

export function resolveVideoEmbed(url: string | null | undefined): VideoEmbedResult {
  const originalUrl = url ?? ''
  if (!isEmbeddableHttpUrl(originalUrl)) {
    return { embedUrl: null, isEmbeddable: false, originalUrl }
  }

  const youtubeMatch = originalUrl.match(YOUTUBE_REGEX)
  if (youtubeMatch) {
    return { embedUrl: `https://www.youtube.com/embed/${youtubeMatch[6]}`, isEmbeddable: true, originalUrl }
  }
  if (VIMEO_REGEX.test(originalUrl)) {
    return {
      embedUrl: originalUrl.replace(VIMEO_REGEX, 'https://player.vimeo.com/video/$4'),
      isEmbeddable: true,
      originalUrl,
    }
  }
  if (DAILYMOTION_REGEX.test(originalUrl)) {
    return {
      embedUrl: originalUrl.replace(DAILYMOTION_REGEX, 'https://www.dailymotion.com/embed/video/$4'),
      isEmbeddable: true,
      originalUrl,
    }
  }
  if (DIRECT_VIDEO_REGEX.test(originalUrl)) {
    return { embedUrl: originalUrl, isEmbeddable: true, originalUrl }
  }

  // Instagram y otras plataformas sin oEmbed público fiable: se mantiene como enlace externo.
  return { embedUrl: null, isEmbeddable: false, originalUrl }
}
